// =============================================================================
// SharedFacility.jsx - Facility Sharing Management Component (PREMIUM ONLY)
// =============================================================================
// Handles facility sharing invitations, configuration sync, and partner management
// When accepting an invitation, automatically copies partner club's pitch configuration
// REQUIRES: Premium subscription tier for both clubs
// NOW SUPPORTS: Multi-facility selection when sending invitations
// =============================================================================

import React, { useState, useEffect } from 'react';
import { db } from '../../../utils/firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  addDoc,
  updateDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import PremiumFeatureModal from '../../PremiumFeatureModal';

/**
 * SharedFacility Component
 * Main component for managing facility sharing between clubs
 * 
 * @param {string} clubId - The current club's ID
 * @param {object} clubInfo - Club information object
 * @param {string} userEmail - Current user's email
 * @param {boolean} hasCompletedSetup - Whether the club has completed the setup wizard
 */
const SharedFacility = ({ clubId, clubInfo, userEmail, hasCompletedSetup = false }) => {
  // ===========================================================================
  // STATE
  // ===========================================================================
  const [sharedFacilityStatus, setSharedFacilityStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Premium subscription state
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  
  // Facilities state (NEW)
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(true);
  
  // Form state
  const [invitedEmail, setInvitedEmail] = useState('');
  const [dayAccessRules, setDayAccessRules] = useState({
    monday: 'both',
    tuesday: 'both',
    wednesday: 'both',
    thursday: 'both',
    friday: 'both',
    saturday: 'both',
    sunday: 'both'
  });
  
  const [pendingInvites, setPendingInvites] = useState([]);

  // ===========================================================================
  // CHECK PREMIUM STATUS
  // ===========================================================================
  const isPremium = subscriptionTier === 'premium';

  // ===========================================================================
  // EFFECTS
  // ===========================================================================
  useEffect(() => {
    if (clubId) {
      loadSubscriptionTier();
      loadSharedFacilityStatus();
      loadPendingInvites();
      loadFacilities();
    }
  }, [clubId]);

  // Sync dayAccessRules state when sharedFacilityStatus loads
  useEffect(() => {
    if (sharedFacilityStatus?.dayAccessRules) {
      setDayAccessRules(sharedFacilityStatus.dayAccessRules);
    }
  }, [sharedFacilityStatus]);

  // Auto-select all facilities when they load
  useEffect(() => {
    if (facilities.length > 0 && selectedFacilities.length === 0) {
      setSelectedFacilities(facilities.map(f => f.id));
    }
  }, [facilities]);

  /**
   * Load subscription tier from Firestore
   */
  const loadSubscriptionTier = async () => {
    if (!clubId) {
      setIsLoadingSubscription(false);
      return;
    }

    try {
      console.log('🔍 Loading subscription tier for club:', clubId);
      const clubDocRef = doc(db, 'clubs', clubId);
      const clubDoc = await getDoc(clubDocRef);
      
      if (clubDoc.exists()) {
        const clubData = clubDoc.data();
        const tier = clubData.subscriptionTier || 'free';
        console.log('📊 Subscription tier loaded:', tier);
        setSubscriptionTier(tier);
      } else {
        console.log('⚠️ Club document not found, defaulting to free tier');
        setSubscriptionTier('free');
      }
    } catch (error) {
      console.error('❌ Error loading subscription tier:', error);
      setSubscriptionTier('free');
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  /**
   * Load facilities from club data
   * Checks multiple sources: facilities subcollection, facilities array, satelliteConfig.facilities, and pitches
   */
  const loadFacilities = async () => {
    try {
      setIsLoadingFacilities(true);
      console.log('🏟️ Loading facilities for club:', clubId);
      
      const clubDocRef = doc(db, 'clubs', clubId);
      const clubDoc = await getDoc(clubDocRef);
      
      if (!clubDoc.exists()) {
        console.log('⚠️ Club document not found');
        setFacilities([]);
        return;
      }
      
      const clubData = clubDoc.data();
      console.log('📋 Club data keys:', Object.keys(clubData));
      
      const facilityMap = new Map();
      
      // SOURCE 1 (PRIMARY): Check for facilities subcollection first
      // Path: clubs/{clubId}/facilities/{facilityId}
      try {
        const facilitiesCollectionRef = collection(db, 'clubs', clubId, 'facilities');
        const facilitiesSnapshot = await getDocs(facilitiesCollectionRef);
        
        console.log('🏟️ Facilities subcollection query complete, docs found:', facilitiesSnapshot.size);
        
        if (facilitiesSnapshot.size > 0) {
          facilitiesSnapshot.forEach(docSnap => {
            const facilityData = docSnap.data();
            const facilityId = docSnap.id;
            
            console.log('🏟️ Found facility in subcollection:', facilityId, facilityData.name);
            
            // Get pitches from the facility document
            const facilityPitches = [];
            if (facilityData.pitches && Array.isArray(facilityData.pitches)) {
              facilityData.pitches.forEach((pitch, index) => {
                facilityPitches.push({
                  id: pitch.id || `pitch-${index + 1}`,
                  name: pitch.name || `Pitch ${index + 1}`
                });
              });
            }
            
            facilityMap.set(facilityId, {
              id: facilityId,
              name: facilityData.name || facilityData.facilityName || `Facility ${facilityMap.size + 1}`,
              pitches: facilityPitches,
              pitchCount: facilityData.pitchCount || facilityPitches.length,
              satelliteImage: facilityData.satelliteImage || null,
              location: facilityData.location || null,
              isPrimary: facilityData.isPrimary || false
            });
          });
        }
      } catch (subCollectionError) {
        console.error('❌ Error querying facilities subcollection:', subCollectionError);
      }
      
      // SOURCE 2: Check for facilities array directly on club document
      if (clubData.facilities && Array.isArray(clubData.facilities)) {
        console.log('🏟️ Found facilities array:', clubData.facilities.length);
        clubData.facilities.forEach(facility => {
          const facilityId = facility.id || facility.facilityId || `facility-${facilityMap.size + 1}`;
          if (!facilityMap.has(facilityId)) {
            facilityMap.set(facilityId, {
              id: facilityId,
              name: facility.name || facility.facilityName || `Facility ${facilityMap.size + 1}`,
              pitches: [],
              satelliteImage: facility.satelliteImage || null,
              location: facility.location || null
            });
          }
        });
      }
      
      // SOURCE 3: Check satelliteConfig.facilities (object keyed by facility ID)
      if (clubData.satelliteConfig?.facilities) {
        console.log('🛰️ Found satelliteConfig.facilities:', Object.keys(clubData.satelliteConfig.facilities));
        Object.entries(clubData.satelliteConfig.facilities).forEach(([facilityId, facilityData]) => {
          if (!facilityMap.has(facilityId)) {
            facilityMap.set(facilityId, {
              id: facilityId,
              name: facilityData.name || facilityData.facilityName || `Facility ${facilityId.replace('facility-', '')}`,
              pitches: [],
              satelliteImage: facilityData.satelliteImage || null,
              location: facilityData.location || null
            });
          } else {
            // Update existing facility with satellite data
            const existing = facilityMap.get(facilityId);
            existing.name = facilityData.name || facilityData.facilityName || existing.name;
            existing.satelliteImage = facilityData.satelliteImage || existing.satelliteImage;
            existing.location = facilityData.location || existing.location;
          }
        });
      }
      
      // SOURCE 4: Extract from pitches array on club doc (group by facilityId)
      const pitches = clubData.pitches || [];
      if (pitches.length > 0) {
        console.log('📋 Found pitches on club doc:', pitches.length);
        
        pitches.forEach(pitch => {
          const facilityId = pitch.facilityId || 'facility-1';
          const facilityName = pitch.facilityName || null;
          
          // If facility doesn't exist yet, create it
          if (!facilityMap.has(facilityId)) {
            facilityMap.set(facilityId, {
              id: facilityId,
              name: facilityName || `Facility ${facilityId.replace('facility-', '')}`,
              pitches: [],
              satelliteImage: pitch.satelliteImage || null
            });
          }
          
          // Add pitch to facility if not already there
          const facility = facilityMap.get(facilityId);
          const pitchExists = facility.pitches.some(p => p.id === pitch.id);
          if (!pitchExists) {
            facility.pitches.push({
              id: pitch.id,
              name: pitch.name || `Pitch ${pitch.id}`
            });
          }
          
          // Update facility name if pitch has one and facility doesn't have a good name
          if (facilityName && (facility.name.startsWith('Facility ') || !facility.name)) {
            facility.name = facilityName;
          }
        });
      }
      
      const facilitiesArray = Array.from(facilityMap.values());
      
      // Sort: primary facility first, then by name
      facilitiesArray.sort((a, b) => {
        if (a.isPrimary && !b.isPrimary) return -1;
        if (!a.isPrimary && b.isPrimary) return 1;
        return a.name.localeCompare(b.name);
      });
      
      console.log('🏟️ Total facilities found:', facilitiesArray.length);
      console.log('🏟️ Facilities:', facilitiesArray.map(f => ({ 
        id: f.id, 
        name: f.name, 
        pitchCount: f.pitchCount || f.pitches.length 
      })));
      
      setFacilities(facilitiesArray);
      
    } catch (error) {
      console.error('❌ Error loading facilities:', error);
      setFacilities([]);
    } finally {
      setIsLoadingFacilities(false);
    }
  };

  /**
   * Load current shared facility status
   */
  const loadSharedFacilityStatus = async () => {
    try {
      setIsLoading(true);
      const statusRef = doc(db, 'clubs', clubId, 'sharedFacility', 'status');
      const statusDoc = await getDoc(statusRef);
      
      if (statusDoc.exists()) {
        setSharedFacilityStatus(statusDoc.data());
      } else {
        setSharedFacilityStatus(null);
      }
    } catch (err) {
      console.error('Error loading shared facility status:', err);
      setError('Failed to load sharing status');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load pending invitations
   */
  const loadPendingInvites = async () => {
    try {
      console.log('📬 Loading pending invites for email:', userEmail);
      
      // Normalize email to lowercase for querying (emails are stored lowercase)
      const normalizedEmail = userEmail?.toLowerCase?.() || '';
      
      // Load invitations sent by this club
      const sentQuery = query(
        collection(db, 'sharedFacilityInvites'),
        where('initiatingClubId', '==', clubId),
        where('status', '==', 'pending')
      );
      
      // Load invitations received by this club
      const receivedQuery = query(
        collection(db, 'sharedFacilityInvites'),
        where('invitedClubEmail', '==', normalizedEmail),
        where('status', '==', 'pending')
      );
      
      const [sentSnapshot, receivedSnapshot] = await Promise.all([
        getDocs(sentQuery),
        getDocs(receivedQuery)
      ]);
      
      console.log('📬 Sent invites found:', sentSnapshot.size);
      console.log('📬 Received invites found:', receivedSnapshot.size);
      
      const invites = [];
      
      sentSnapshot.forEach((doc) => {
        invites.push({
          id: doc.id,
          type: 'sent',
          ...doc.data()
        });
      });
      
      receivedSnapshot.forEach((doc) => {
        invites.push({
          id: doc.id,
          type: 'received',
          ...doc.data()
        });
      });
      
      console.log('📬 Total pending invites:', invites.length);
      setPendingInvites(invites);
    } catch (error) {
      console.error('❌ Error loading pending invites:', error);
    }
  };

  /**
   * Copy pitch configuration from partner club (UPDATED for facility selection)
   * This includes: pitch names, sections, satellite imagery, reference points
   */
  const copyPartnerPitchConfiguration = async (partnerClubId, sharedFacilityIds = null) => {
    try {
      console.log('📋 Copying pitch configuration from partner club:', partnerClubId);
      console.log('📋 Shared facility IDs:', sharedFacilityIds);
      
      // Step 1: Load partner club document (contains pitches array)
      const partnerClubRef = doc(db, 'clubs', partnerClubId);
      const partnerClubDoc = await getDoc(partnerClubRef);
      
      if (!partnerClubDoc.exists()) {
        throw new Error('Partner club not found');
      }
      
      const partnerClubData = partnerClubDoc.data();
      console.log('📋 Partner club data loaded:', partnerClubData);
      console.log('📡 Partner pitches:', partnerClubData.pitches);
      
      // Step 2: Load partner club's facilities subcollection
      console.log('🏟️ Loading partner facilities subcollection...');
      const partnerFacilitiesRef = collection(db, 'clubs', partnerClubId, 'facilities');
      const partnerFacilitiesSnapshot = await getDocs(partnerFacilitiesRef);
      
      const partnerFacilities = [];
      partnerFacilitiesSnapshot.forEach(docSnap => {
        partnerFacilities.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      console.log('🏟️ Partner facilities found:', partnerFacilities.length);
      
      // Step 3: Load partner club's settings
      const partnerSettingsRef = doc(db, 'clubs', partnerClubId, 'settings', 'general');
      const partnerSettingsDoc = await getDoc(partnerSettingsRef);
      
      if (!partnerSettingsDoc.exists()) {
        console.warn('⚠️ Partner club settings not found, continuing with facilities copy');
      }
      
      const partnerSettings = partnerSettingsDoc.exists() ? partnerSettingsDoc.data() : {};
      console.log('📋 Partner settings loaded:', partnerSettings);
      
      // Step 4: Filter facilities by shared facility IDs (if specified)
      let facilitiesToCopy = partnerFacilities;
      if (sharedFacilityIds && sharedFacilityIds.length > 0) {
        console.log('🔍 Filtering facilities by shared facility IDs:', sharedFacilityIds);
        facilitiesToCopy = partnerFacilities.filter(f => sharedFacilityIds.includes(f.id));
        console.log('🏟️ Filtered facilities count:', facilitiesToCopy.length);
      }
      
      // Step 5: Copy facilities subcollection to our club
      if (facilitiesToCopy.length > 0) {
        console.log('🏟️ Copying facilities subcollection...');
        const copiedAtTimestamp = new Date().toISOString();
        
        for (const facility of facilitiesToCopy) {
          const ourFacilityRef = doc(db, 'clubs', clubId, 'facilities', facility.id);
          
          const facilityData = {
            ...facility,
            copiedFrom: partnerClubId,
            copiedAt: copiedAtTimestamp,
            isReadOnly: true
          };
          delete facilityData.id; // Don't store id as a field
          
          await setDoc(ourFacilityRef, facilityData);
          console.log('✅ Copied facility:', facility.id, facility.name);
        }
        
        // Update club document with facility count and primary facility
        const ourClubRef = doc(db, 'clubs', clubId);
        await setDoc(ourClubRef, {
          facilityCount: facilitiesToCopy.length,
          primaryFacilityId: facilitiesToCopy[0]?.id || 'facility-1',
          facilitiesCopiedFrom: partnerClubId,
          facilitiesCopiedAt: copiedAtTimestamp
        }, { merge: true });
        
        console.log('✅ Facilities subcollection copied successfully');
      } else {
        console.log('ℹ️ No facilities in subcollection to copy');
      }
      
      // Step 6: Also copy pitches from club document (for backwards compatibility)
      let pitchesToCopy = partnerClubData.pitches || [];
      
      if (sharedFacilityIds && sharedFacilityIds.length > 0 && pitchesToCopy.length > 0) {
        console.log('🔍 Filtering pitches by shared facilities:', sharedFacilityIds);
        pitchesToCopy = pitchesToCopy.filter(pitch => {
          const pitchFacilityId = pitch.facilityId || 'facility-1';
          return sharedFacilityIds.includes(pitchFacilityId);
        });
        console.log('📋 Filtered pitches count:', pitchesToCopy.length);
      }
      
      // Copy pitches array AND satellite image from partner's club document
      if (pitchesToCopy.length > 0) {
        console.log('📡 Copying pitches and satellite image from partner club document...');
        const ourClubRef = doc(db, 'clubs', clubId);
        
        const copiedAtTimestamp = new Date().toISOString();
        
        const updateData = {
          pitches: pitchesToCopy.map(pitch => ({
            ...pitch,
            copiedFrom: partnerClubId,
            copiedAt: copiedAtTimestamp,
            isReadOnly: true
          })),
          pitchCount: pitchesToCopy.length,
          facilityConfigured: true,
          pitchesLastUpdated: serverTimestamp()
        };
        
        // Also copy satellite image if it exists
        if (partnerClubData.satelliteImage) {
          console.log('📡 Copying satellite image from partner club...');
          updateData.satelliteImage = partnerClubData.satelliteImage;
          updateData.imageWidth = partnerClubData.imageWidth;
          updateData.imageHeight = partnerClubData.imageHeight;
          updateData.satelliteImageCapturedAt = partnerClubData.satelliteImageCapturedAt;
          console.log('✅ Satellite image data prepared for copy');
        }
        
        // Copy satelliteConfig if it exists
        if (partnerClubData.satelliteConfig) {
          const filteredSatelliteConfig = { ...partnerClubData.satelliteConfig };
          
          if (sharedFacilityIds && filteredSatelliteConfig.facilities) {
            const filteredFacilities = {};
            Object.entries(filteredSatelliteConfig.facilities).forEach(([facId, facData]) => {
              if (sharedFacilityIds.includes(facId)) {
                filteredFacilities[facId] = facData;
              }
            });
            filteredSatelliteConfig.facilities = filteredFacilities;
          }
          
          updateData.satelliteConfig = filteredSatelliteConfig;
          console.log('📡 SatelliteConfig prepared for copy');
        }
        
        await setDoc(ourClubRef, updateData, { merge: true });
        
        console.log('✅ Pitches copied to our club document');
        console.log('📡 Number of pitches copied:', pitchesToCopy.length);
      } else {
        console.log('ℹ️ No pitches in club document to copy');
      }
      
      // Step 7: Copy pitch configuration to settings
      try {
        const copiedAtTimestamp = new Date().toISOString();
        
        // Get all pitch IDs from both facilities and club document
        const allPitchIds = [
          ...pitchesToCopy.map(p => p.id),
          ...facilitiesToCopy.flatMap(f => (f.pitches || []).map(p => p.id))
        ];
        
        const filterByPitch = (obj) => {
          if (!obj) return {};
          const filtered = {};
          Object.entries(obj).forEach(([key, value]) => {
            if (allPitchIds.some(pitchId => key.includes(pitchId) || key === pitchId)) {
              filtered[key] = value;
            }
          });
          return filtered;
        };
        
        const configToCopy = {
          pitchNames: sharedFacilityIds ? filterByPitch(partnerSettings.pitchNames) : (partnerSettings.pitchNames || {}),
          pitchReferencePoints: sharedFacilityIds ? filterByPitch(partnerSettings.pitchReferencePoints) : (partnerSettings.pitchReferencePoints || {}),
          pitchSectionConfigurations: sharedFacilityIds ? filterByPitch(partnerSettings.pitchSectionConfigurations) : (partnerSettings.pitchSectionConfigurations || {}),
          showGrassArea: sharedFacilityIds ? filterByPitch(partnerSettings.showGrassArea) : (partnerSettings.showGrassArea || {}),
          copiedFrom: partnerClubId,
          copiedAt: copiedAtTimestamp,
          lastUpdated: copiedAtTimestamp
        };
        
        const ourSettingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
        await setDoc(ourSettingsRef, configToCopy, { merge: true });
        console.log('✅ Settings updated with partner configuration');
      } catch (settingsError) {
        console.warn('⚠️ Could not copy settings (non-critical):', settingsError.message);
      }
      
      console.log('✅ Pitch configuration copied successfully');
      return { success: true };
      
    } catch (error) {
      console.error('❌ Error copying pitch configuration:', error);
      throw new Error(`Failed to copy pitch configuration: ${error.message}`);
    }
  };

  /**
   * Send invitation to another club (UPDATED with facility selection)
   */
  const sendInvitation = async (invitedClubEmail, dayAccessRules, selectedFacilityIds) => {
    try {
      // Validate email
      if (!invitedClubEmail || !invitedClubEmail.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      // Validate facility selection
      if (!selectedFacilityIds || selectedFacilityIds.length === 0) {
        throw new Error('Please select at least one facility to share');
      }

      // Check if already sharing
      if (sharedFacilityStatus && !sharedFacilityStatus.inCoolingOff) {
        throw new Error('You are already sharing your facility with another club');
      }

      // Check if in cooling off period
      if (sharedFacilityStatus?.inCoolingOff) {
        throw new Error('You are in a cooling off period. Please wait until it expires.');
      }

      // Get facility details for the invitation
      const selectedFacilityDetails = facilities
        .filter(f => selectedFacilityIds.includes(f.id))
        .map(f => ({
          id: f.id,
          name: f.name,
          pitchCount: f.pitches.length
        }));

      // Create invitation - store email in lowercase for consistent matching
      const inviteData = {
        initiatingClubId: clubId,
        initiatingClubName: clubInfo?.clubName || clubInfo?.name || 'Unknown Club',
        initiatingClubEmail: userEmail?.toLowerCase(),
        invitedClubEmail: invitedClubEmail.toLowerCase(), // Always lowercase
        dayAccessRules: dayAccessRules,
        // Facility selection data
        sharedFacilityIds: selectedFacilityIds,
        sharedFacilities: selectedFacilityDetails,
        allFacilitiesShared: selectedFacilityIds.length === facilities.length,
        status: 'pending',
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days
      };

      console.log('📨 Creating invitation with facility selection:', inviteData);

      await addDoc(collection(db, 'sharedFacilityInvites'), inviteData);
      
      setSuccessMessage(`Invitation sent successfully! Sharing ${selectedFacilityIds.length} facility(s).`);
      setInvitedEmail('');
      await loadPendingInvites();
      
      return { success: true };
    } catch (error) {
      console.error('Error sending invitation:', error);
      throw error;
    }
  };

  /**
   * Accept an invitation (UPDATED to handle facility selection)
   */
  const acceptInvitation = async (inviteId, invite) => {
    try {
      setIsSaving(true);
      setError(null);

      // SAFETY CHECK: Ensure club has completed setup wizard
      if (!hasCompletedSetup) {
        throw new Error('Please complete the club setup wizard before accepting facility sharing invitations. This ensures your teams and settings are configured properly.');
      }

      console.log('📨 Accepting invitation:', inviteId, invite);
      console.log('📋 Shared facilities:', invite.sharedFacilityIds || 'All');

      // Step 1: Copy pitch configuration from initiating club (with facility filter)
      await copyPartnerPitchConfiguration(
        invite.initiatingClubId, 
        invite.sharedFacilityIds || null // Pass facility IDs if specified
      );

      // Step 2: Create shared facility status for ACCEPTING club (this club)
      console.log('📝 Creating shared facility status for our club:', clubId);
      console.log('📝 Partner club ID:', invite.initiatingClubId);
      console.log('📝 Our role: invited');
      
      const statusData = {
        isSharing: true,
        partnerClubId: invite.initiatingClubId,
        partnerClubName: invite.initiatingClubName,
        partnerClubEmail: invite.initiatingClubEmail,
        dayAccessRules: invite.dayAccessRules,
        role: 'invited', // This club was invited
        // NEW: Store which facilities are shared
        sharedFacilityIds: invite.sharedFacilityIds || [],
        sharedFacilities: invite.sharedFacilities || [],
        allFacilitiesShared: invite.allFacilitiesShared || false,
        startedAt: serverTimestamp(),
        inCoolingOff: false
      };
      
      console.log('📝 Status data to write:', statusData);
      
      const ourStatusRef = doc(db, 'clubs', clubId, 'sharedFacility', 'status');
      
      try {
        await setDoc(ourStatusRef, statusData);
        console.log('✅ Our shared facility status created');
      } catch (statusError) {
        console.error('❌ Failed to create our status:', statusError);
        throw statusError;
      }

      // Step 3: Update shared facility status for INITIATING club
      const theirStatusRef = doc(db, 'clubs', invite.initiatingClubId, 'sharedFacility', 'status');
      
      // Load initiating club's info
      const ourClubDoc = await getDoc(doc(db, 'clubs', clubId));
      const ourClubData = ourClubDoc.data();
      
      await setDoc(theirStatusRef, {
        isSharing: true,
        partnerClubId: clubId,
        partnerClubName: ourClubData?.clubName || ourClubData?.name || 'Unknown Club',
        partnerClubEmail: userEmail,
        dayAccessRules: invite.dayAccessRules,
        role: 'initiator', // They initiated
        // NEW: Store which facilities are shared
        sharedFacilityIds: invite.sharedFacilityIds || [],
        sharedFacilities: invite.sharedFacilities || [],
        allFacilitiesShared: invite.allFacilitiesShared || false,
        startedAt: serverTimestamp(),
        inCoolingOff: false
      });

      // Step 4: Update invitation status
      const inviteRef = doc(db, 'sharedFacilityInvites', inviteId);
      await updateDoc(inviteRef, {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
        acceptedByClubId: clubId
      });

      setSuccessMessage('Invitation accepted! Facility is now shared.');
      await loadSharedFacilityStatus();
      await loadPendingInvites();

      return { success: true };
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError(error.message || 'Failed to accept invitation');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Decline an invitation
   */
  const declineInvitation = async (inviteId) => {
    try {
      setIsSaving(true);
      const inviteRef = doc(db, 'sharedFacilityInvites', inviteId);
      await updateDoc(inviteRef, {
        status: 'declined',
        declinedAt: serverTimestamp()
      });

      setSuccessMessage('Invitation declined');
      await loadPendingInvites();
    } catch (error) {
      console.error('Error declining invitation:', error);
      setError('Failed to decline invitation');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Terminate sharing arrangement
   */
  const terminateSharing = async () => {
    if (!window.confirm('Are you sure you want to terminate this sharing arrangement? A 2-day cooling off period will begin.')) {
      return;
    }

    try {
      setIsSaving(true);
      
      const coolingOffEndTime = new Date();
      coolingOffEndTime.setDate(coolingOffEndTime.getDate() + 2); // 2-day cooling off

      // Update our status
      const ourStatusRef = doc(db, 'clubs', clubId, 'sharedFacility', 'status');
      await updateDoc(ourStatusRef, {
        isSharing: false,
        inCoolingOff: true,
        coolingOffStartedAt: serverTimestamp(),
        coolingOffEndsAt: coolingOffEndTime,
        terminatedBy: clubId
      });

      // Update partner's status
      const partnerStatusRef = doc(db, 'clubs', sharedFacilityStatus.partnerClubId, 'sharedFacility', 'status');
      await updateDoc(partnerStatusRef, {
        isSharing: false,
        inCoolingOff: true,
        coolingOffStartedAt: serverTimestamp(),
        coolingOffEndsAt: coolingOffEndTime,
        terminatedBy: clubId
      });

      setSuccessMessage('Sharing arrangement terminated. 2-day cooling off period started.');
      await loadSharedFacilityStatus();
    } catch (error) {
      console.error('Error terminating sharing:', error);
      setError('Failed to terminate sharing arrangement');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Update day access rules
   */
  const updateDayAccessRules = async () => {
    try {
      setIsSaving(true);
      
      // Update our status
      const ourStatusRef = doc(db, 'clubs', clubId, 'sharedFacility', 'status');
      await updateDoc(ourStatusRef, {
        dayAccessRules: dayAccessRules,
        lastUpdated: serverTimestamp()
      });

      // Update partner's status
      const partnerStatusRef = doc(db, 'clubs', sharedFacilityStatus.partnerClubId, 'sharedFacility', 'status');
      await updateDoc(partnerStatusRef, {
        dayAccessRules: dayAccessRules,
        lastUpdated: serverTimestamp()
      });

      setSuccessMessage('Day access rules updated successfully');
      await loadSharedFacilityStatus();
    } catch (error) {
      console.error('Error updating day access rules:', error);
      setError('Failed to update day access rules');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle expand toggle with premium check
   */
  const handleExpandToggle = () => {
    if (!isPremium && !isExpanded) {
      console.log('🚫 Non-premium user attempting to access facility sharing');
      setShowPaywallModal(true);
      return;
    }
    setIsExpanded(!isExpanded);
  };

  /**
   * Handle form submission (UPDATED to include facility selection)
   */
  const handleSubmitInvitation = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      await sendInvitation(invitedEmail, dayAccessRules, selectedFacilities);
    } catch (err) {
      setError(err.message || 'Failed to send invitation');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Toggle facility selection
   */
  const toggleFacilitySelection = (facilityId) => {
    setSelectedFacilities(prev => {
      if (prev.includes(facilityId)) {
        return prev.filter(id => id !== facilityId);
      } else {
        return [...prev, facilityId];
      }
    });
  };

  /**
   * Select/deselect all facilities
   */
  const toggleAllFacilities = () => {
    if (selectedFacilities.length === facilities.length) {
      setSelectedFacilities([]);
    } else {
      setSelectedFacilities(facilities.map(f => f.id));
    }
  };

  // ===========================================================================
  // RENDER: Loading State
  // ===========================================================================
  if (isLoadingSubscription || isLoading) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span style={{ fontSize: '14px', color: '#6b7280' }}>
            Loading facility sharing...
          </span>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // ===========================================================================
  // RENDER: Main Interface
  // ===========================================================================
  return (
    <>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        {/* Header with Premium Badge */}
        <div
          onClick={handleExpandToggle}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '8px',
            margin: '-8px',
            borderRadius: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
              Facility Sharing
            </h3>
            
            {/* Premium Badge */}
            <div style={{
              background: isPremium 
                ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
                : 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)',
              padding: '4px 12px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{ fontSize: '12px' }}>👑</span>
              <span style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'white'
              }}>
                {isPremium ? 'PREMIUM' : 'PREMIUM ONLY'}
              </span>
            </div>
          </div>

          <div style={{
            fontSize: '20px',
            color: '#6b7280',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </div>
        </div>

        <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
          {isPremium 
            ? 'Share your facility with another club while maintaining complete control'
            : 'Upgrade to Premium to share your facility with other clubs'
          }
        </p>

        {/* Expanded Content (Premium Only) */}
        {isExpanded && isPremium && (
          <div style={{ marginTop: '24px' }}>
            {/* Success/Error Messages */}
            {successMessage && (
              <div style={{
                backgroundColor: '#d1fae5',
                border: '1px solid #34d399',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                color: '#065f46',
                fontSize: '14px'
              }}>
                {successMessage}
              </div>
            )}

            {error && (
              <div style={{
                backgroundColor: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                color: '#991b1b',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            {/* Pending Invitations */}
            {pendingInvites.length > 0 && (
              <PendingInvitations
                invites={pendingInvites}
                clubId={clubId}
                onAccept={acceptInvitation}
                onDecline={declineInvitation}
                isSaving={isSaving}
                hasCompletedSetup={hasCompletedSetup}
              />
            )}

            {/* Active Sharing Display */}
            {sharedFacilityStatus?.isSharing && !sharedFacilityStatus.inCoolingOff && (
              <ActiveSharingDisplay
                status={sharedFacilityStatus}
                clubName={clubInfo?.clubName || clubInfo?.name || 'Your Club'}
                onTerminate={terminateSharing}
                onUpdateSchedule={updateDayAccessRules}
                dayAccessRules={dayAccessRules}
                setDayAccessRules={setDayAccessRules}
                isSaving={isSaving}
              />
            )}

            {/* Cooling Off Display */}
            {sharedFacilityStatus?.inCoolingOff && (
              <CoolingOffDisplay status={sharedFacilityStatus} />
            )}

            {/* Invitation Form (only if not currently sharing) */}
            {!sharedFacilityStatus?.isSharing && !sharedFacilityStatus?.inCoolingOff && (
              <InvitationForm
                invitedEmail={invitedEmail}
                setInvitedEmail={setInvitedEmail}
                dayAccessRules={dayAccessRules}
                setDayAccessRules={setDayAccessRules}
                facilities={facilities}
                selectedFacilities={selectedFacilities}
                onToggleFacility={toggleFacilitySelection}
                onToggleAllFacilities={toggleAllFacilities}
                isLoadingFacilities={isLoadingFacilities}
                onSubmit={handleSubmitInvitation}
                isSaving={isSaving}
              />
            )}
          </div>
        )}
      </div>

      {/* Premium Paywall Modal */}
      <PremiumFeatureModal
        isOpen={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
        featureName="Facility Sharing"
        currentTier={subscriptionTier}
        requiredTiers={['premium']}
        benefits={[
          "Invite other Premium clubs to share your facility",
          "Choose which facilities to share (or share all)",
          "Configure day-based access rules for each club",
          "Automatic conflict prevention between shared clubs",
          "Partner clubs inherit your pitch layout automatically",
          "Safe termination with 2-day cooling-off periods",
          "Complete data sovereignty for each club"
        ]}
        upgradeUrl="/pricing"
      />
    </>
  );
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * Active Sharing Display Component (UPDATED to show shared facilities)
 */
const ActiveSharingDisplay = ({ status, clubName, onTerminate, onUpdateSchedule, dayAccessRules, setDayAccessRules, isSaving }) => {
  // Determine host and guest names based on role
  const isHost = status.role === 'initiator';
  const hostClubName = isHost ? clubName : status.partnerClubName;
  const guestClubName = isHost ? status.partnerClubName : clubName;

  return (
    <div style={{
      backgroundColor: '#d1fae5',
      border: '2px solid #10b981',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
        <div>
          <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#065f46', margin: '0 0 8px 0' }}>
            ✅ Currently Sharing Facility
          </h4>
          <p style={{ fontSize: '14px', color: '#047857', margin: '0 0 4px 0' }}>
            Partner: <strong>{status.partnerClubName}</strong>
          </p>
          <p style={{ fontSize: '12px', color: '#059669', margin: 0 }}>
            Contact: {status.partnerClubEmail}
          </p>
          <p style={{ fontSize: '12px', color: '#047857', margin: '4px 0 0 0' }}>
            Your role: <strong>{isHost ? 'Host (facility owner)' : 'Guest'}</strong>
          </p>
          {/* Show shared facilities info */}
          {status.sharedFacilities && status.sharedFacilities.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <p style={{ fontSize: '12px', color: '#059669', margin: '0 0 4px 0', fontWeight: '500' }}>
                🏟️ Shared Facilities:
              </p>
              <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px', fontSize: '12px', color: '#047857' }}>
                {status.sharedFacilities.map(facility => (
                  <li key={facility.id}>
                    {facility.name} ({facility.pitchCount} pitch{facility.pitchCount !== 1 ? 'es' : ''})
                  </li>
                ))}
              </ul>
            </div>
          )}
          {status.allFacilitiesShared && (
            <p style={{ fontSize: '12px', color: '#059669', marginTop: '4px', fontStyle: 'italic' }}>
              All facilities are shared
            </p>
          )}
        </div>
        <button
          onClick={onTerminate}
          disabled={isSaving}
          style={{
            padding: '8px 16px',
            backgroundColor: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            opacity: isSaving ? 0.6 : 1
          }}
        >
          Terminate Sharing
        </button>
      </div>

      <div style={{ marginTop: '20px' }}>
        <DayAccessSchedule
          dayAccessRules={dayAccessRules}
          setDayAccessRules={setDayAccessRules}
          onUpdate={onUpdateSchedule}
          isSaving={isSaving}
          hostClubName={hostClubName}
          guestClubName={guestClubName}
          isHostView={isHost}
        />
      </div>
    </div>
  );
};

/**
 * Cooling Off Display Component
 */
const CoolingOffDisplay = ({ status }) => {
  const endsAt = status.coolingOffEndsAt?.toDate?.() || new Date(status.coolingOffEndsAt);
  
  return (
    <div style={{
      backgroundColor: '#fef3c7',
      border: '2px solid #f59e0b',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px'
    }}>
      <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#92400e', margin: '0 0 12px 0' }}>
        ⏳ Cooling Off Period Active
      </h4>
      <p style={{ fontSize: '14px', color: '#78350f', margin: '0 0 8px 0' }}>
        The sharing arrangement has been terminated. Neither club can share facilities until the cooling off period ends.
      </p>
      <p style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', margin: 0 }}>
        Ends: {endsAt.toLocaleDateString()} at {endsAt.toLocaleTimeString()}
      </p>
    </div>
  );
};

/**
 * Facility Selector Component (NEW)
 */
const FacilitySelector = ({ 
  facilities, 
  selectedFacilities, 
  onToggleFacility, 
  onToggleAllFacilities,
  isLoading 
}) => {
  if (isLoading) {
    return (
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          color: '#374151',
          marginBottom: '8px'
        }}>
          Select Facilities to Share
        </label>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>Loading facilities...</div>
      </div>
    );
  }

  if (facilities.length === 0) {
    return (
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          color: '#374151',
          marginBottom: '8px'
        }}>
          Select Facilities to Share
        </label>
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#fef3c7', 
          borderRadius: '6px',
          fontSize: '14px',
          color: '#92400e'
        }}>
          ⚠️ No facilities configured. Please set up your pitches first.
        </div>
      </div>
    );
  }

  const allSelected = selectedFacilities.length === facilities.length;

  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: '#374151',
        marginBottom: '8px'
      }}>
        Select Facilities to Share <span style={{ color: '#ef4444' }}>*</span>
      </label>
      
      {/* Select All Toggle */}
      <div 
        onClick={onToggleAllFacilities}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 12px',
          backgroundColor: allSelected ? '#dbeafe' : '#f9fafb',
          border: `2px solid ${allSelected ? '#3b82f6' : '#e5e7eb'}`,
          borderRadius: '8px',
          cursor: 'pointer',
          marginBottom: '8px',
          transition: 'all 0.2s'
        }}
      >
        <div style={{
          width: '20px',
          height: '20px',
          borderRadius: '4px',
          border: `2px solid ${allSelected ? '#3b82f6' : '#d1d5db'}`,
          backgroundColor: allSelected ? '#3b82f6' : 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {allSelected && <span style={{ color: 'white', fontSize: '14px' }}>✓</span>}
        </div>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
          Share All Facilities ({facilities.length})
        </span>
      </div>

      {/* Individual Facility Checkboxes */}
      <div style={{ display: 'grid', gap: '8px' }}>
        {facilities.map(facility => {
          const isSelected = selectedFacilities.includes(facility.id);
          return (
            <div
              key={facility.id}
              onClick={() => onToggleFacility(facility.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                backgroundColor: isSelected ? '#f0fdf4' : 'white',
                border: `2px solid ${isSelected ? '#10b981' : '#e5e7eb'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '4px',
                border: `2px solid ${isSelected ? '#10b981' : '#d1d5db'}`,
                backgroundColor: isSelected ? '#10b981' : 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {isSelected && <span style={{ color: 'white', fontSize: '14px' }}>✓</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                  🏟️ {facility.name}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  {facility.pitches.length} pitch{facility.pitches.length !== 1 ? 'es' : ''}: {facility.pitches.map(p => p.name).join(', ')}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p style={{ fontSize: '12px', color: '#6b7280', margin: '8px 0 0 0' }}>
        The invited club will receive access to the selected facilities and their pitch configurations
      </p>
    </div>
  );
};

/**
 * Invitation Form Component (UPDATED with facility selector)
 */
const InvitationForm = ({ 
  invitedEmail, 
  setInvitedEmail, 
  dayAccessRules, 
  setDayAccessRules, 
  facilities,
  selectedFacilities,
  onToggleFacility,
  onToggleAllFacilities,
  isLoadingFacilities,
  onSubmit, 
  isSaving 
}) => (
  <div style={{
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px'
  }}>
    <form onSubmit={onSubmit}>
      <h4 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
        marginTop: 0,
        marginBottom: '16px'
      }}>
        Invite Another Club
      </h4>
      
      {/* Email Input */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          color: '#374151',
          marginBottom: '8px'
        }}>
          Club Admin Email <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <input
          type="email"
          value={invitedEmail}
          onChange={(e) => setInvitedEmail(e.target.value)}
          placeholder="admin@otherclub.com"
          required
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />
        <p style={{ fontSize: '12px', color: '#92400e', margin: '4px 0 0 0', fontWeight: '500' }}>
          ⚠️ Both clubs must have Premium subscriptions to share facilities
        </p>
      </div>

      {/* Facility Selector (NEW) */}
      <FacilitySelector
        facilities={facilities}
        selectedFacilities={selectedFacilities}
        onToggleFacility={onToggleFacility}
        onToggleAllFacilities={onToggleAllFacilities}
        isLoading={isLoadingFacilities}
      />

      {/* Day Access Schedule */}
      <DayAccessSchedule
        dayAccessRules={dayAccessRules}
        setDayAccessRules={setDayAccessRules}
      />

      <button
        type="submit"
        disabled={isSaving || !invitedEmail || selectedFacilities.length === 0}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: (isSaving || !invitedEmail || selectedFacilities.length === 0) ? '#9ca3af' : '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: (isSaving || !invitedEmail || selectedFacilities.length === 0) ? 'not-allowed' : 'pointer',
          marginTop: '16px'
        }}
      >
        {isSaving ? 'Sending Invitation...' : `Send Invitation (${selectedFacilities.length} ${selectedFacilities.length === 1 ? 'facility' : 'facilities'})`}
      </button>

      <div style={{
        marginTop: '16px',
        padding: '12px',
        backgroundColor: '#eff6ff',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#1e40af',
        lineHeight: '1.6'
      }}>
        <strong>Note:</strong> Once accepted, the invited club will use your pitch configuration for the selected facilities. 
        Both clubs will see each other's allocations in real-time. Either club can terminate the arrangement with a 2-day cooling off period.
      </div>
    </form>
  </div>
);

/**
 * Day Access Schedule Component
 * @param {string} hostClubName - Optional: The name of the host/inviting club
 * @param {string} guestClubName - Optional: The name of the guest/invited club
 * @param {boolean} isHostView - Whether this is being viewed by the host club
 */
const DayAccessSchedule = ({ 
  dayAccessRules, 
  setDayAccessRules, 
  onUpdate, 
  isSaving,
  hostClubName = null,
  guestClubName = null,
  isHostView = true
}) => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  // Use actual club names if provided, otherwise use clear generic labels
  const hostLabel = hostClubName || (isHostView ? 'Your Club' : 'Host Club');
  const guestLabel = guestClubName || (isHostView ? 'Partner Club' : 'Your Club');
  
  const accessOptions = [
    { value: 'my_club', label: `${hostLabel} Only` },
    { value: 'their_club', label: `${guestLabel} Only` },
    { value: 'both', label: 'Both Clubs' }
  ];

  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: '#374151',
        marginBottom: '12px'
      }}>
        Day Access Schedule (Optional)
      </label>
      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
        Define which club has priority access on specific days. This helps organize training schedules.
      </p>

      <div style={{ display: 'grid', gap: '12px' }}>
        {days.map((day) => (
          <div key={day} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              {day}
            </label>
            <select
              value={dayAccessRules[day.toLowerCase()] || 'both'}
              onChange={(e) => setDayAccessRules({
                ...dayAccessRules,
                [day.toLowerCase()]: e.target.value
              })}
              style={{
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              {accessOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {onUpdate && (
        <button
          onClick={onUpdate}
          disabled={isSaving}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            backgroundColor: isSaving ? '#9ca3af' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: isSaving ? 'not-allowed' : 'pointer'
          }}
        >
          {isSaving ? 'Updating...' : 'Update Schedule'}
        </button>
      )}
    </div>
  );
};

/**
 * Pending Invitations Display (UPDATED to show facility info and setup check)
 */
const PendingInvitations = ({ invites, clubId, onAccept, onDecline, isSaving, hasCompletedSetup }) => (
  <div style={{
    backgroundColor: '#fef3c7',
    border: '2px solid #f59e0b',
    borderRadius: '12px',
    padding: '24px',
    marginTop: '0',
    marginBottom: '24px'
  }}>
    <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#92400e', marginTop: 0, marginBottom: '16px' }}>
      📬 Pending Invitations
    </h3>

    {/* Setup Required Warning */}
    {!hasCompletedSetup && invites.some(i => i.type === 'received') && (
      <div style={{
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px'
      }}>
        <span style={{ fontSize: '20px' }}>🚧</span>
        <div>
          <p style={{ fontSize: '14px', fontWeight: '600', color: '#991b1b', margin: '0 0 4px 0' }}>
            Setup Required
          </p>
          <p style={{ fontSize: '13px', color: '#7f1d1d', margin: 0, lineHeight: '1.5' }}>
            You need to complete the club setup wizard before accepting facility sharing invitations. 
            This ensures your teams and other settings are configured properly.
          </p>
          <a 
            href="/setup" 
            style={{
              display: 'inline-block',
              marginTop: '8px',
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600'
            }}
          >
            Complete Setup →
          </a>
        </div>
      </div>
    )}

    {invites.map((invite) => {
      const isSent = invite.type === 'sent';
      const canAccept = hasCompletedSetup && !isSaving;

      return (
        <div key={invite.id} style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '12px'
        }}>
          <div style={{ marginBottom: '12px' }}>
            <p style={{ fontSize: '14px', color: '#1f2937', margin: '0 0 4px 0' }}>
              {isSent ? (
                <>Invitation sent to: <strong>{invite.invitedClubEmail}</strong></>
              ) : (
                <>Invitation from: <strong>{invite.initiatingClubName}</strong></>
              )}
            </p>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
              Sent: {invite.createdAt?.toDate?.().toLocaleDateString() || 'Unknown'}
            </p>
            
            {/* Show shared facilities info */}
            {invite.sharedFacilities && invite.sharedFacilities.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <p style={{ fontSize: '12px', color: '#1e40af', margin: '0 0 4px 0', fontWeight: '500' }}>
                  🏟️ Facilities being shared:
                </p>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px', fontSize: '12px', color: '#374151' }}>
                  {invite.sharedFacilities.map(facility => (
                    <li key={facility.id}>
                      {facility.name} ({facility.pitchCount} pitch{facility.pitchCount !== 1 ? 'es' : ''})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {invite.allFacilitiesShared && (
              <p style={{ fontSize: '12px', color: '#059669', marginTop: '4px', fontStyle: 'italic' }}>
                ✅ All facilities included
              </p>
            )}
            
            {!isSent && (
              <p style={{ fontSize: '12px', color: '#1e40af', margin: '8px 0 0 0', fontStyle: 'italic' }}>
                ⚠️ Accepting will replace your pitch configuration with theirs
              </p>
            )}
          </div>

          {!isSent && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => onAccept(invite.id, invite)}
                disabled={!canAccept}
                title={!hasCompletedSetup ? 'Complete setup wizard first' : ''}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: canAccept ? '#10b981' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: canAccept ? 'pointer' : 'not-allowed',
                  opacity: canAccept ? 1 : 0.6
                }}
              >
                {!hasCompletedSetup ? '🔒 Accept' : 'Accept'}
              </button>
              <button
                onClick={() => onDecline(invite.id)}
                disabled={isSaving}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.6 : 1
                }}
              >
                Decline
              </button>
            </div>
          )}
        </div>
      );
    })}
  </div>
);

export default SharedFacility;
